import React from 'react';
import { Box, Stack, Typography, IconButton, Link as MUILink } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';

interface MessageProps {
  message: any;
}

// helpers
const pickAudioUrl = (content: any = {}) =>
  content?.media?.s3_object?.url ||
  content?.media?.url ||
  content?.url ||
  '';

const pickFilename = (content: any = {}) =>
  content?.filename ||
  content?.media?.s3_object?.filename ||
  content?.media?.filename ||
  'audio.mp3';

const pickFilesize = (content: any = {}) => {
  const bytes = content?.media?.file_size ?? content?.file_size;
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const MessageAudio: React.FC<MessageProps> = ({ message }) => {
  const audioUrl = pickAudioUrl(message?.content);
  const filename = pickFilename(message?.content);
  const sizeText = pickFilesize(message?.content);

  if (!audioUrl) {
    console.warn('Empty audio URL:', audioUrl);
    return null;
  }

  return (
    <Box mb={1}>
      {/* Header: icon + name + download */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AudiotrackIcon fontSize="small" />
        <Typography
          variant="subtitle2"
          noWrap
          title={filename}
          sx={{ flex: 1, fontWeight: 600 }}
        >
          {filename}
        </Typography>
        <MUILink href={audioUrl} target="_blank" rel="noopener noreferrer" underline="none">
          <IconButton size="small">
            <CloudDownloadIcon fontSize="small" />
          </IconButton>
        </MUILink>
      </Stack>

      {/* Audio player */}
      <audio controls style={{ width: '100%' }}>
        <source src={audioUrl} type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>

      {/* File size below player */}
      {sizeText && (
        <Typography variant="caption" sx={{ color: '#666', mt: 0.5 }}>
          Size: {sizeText}
        </Typography>
      )}
    </Box>
  );
};

export default MessageAudio;
